import { useCallback, useSyncExternalStore } from 'react';
import type { IcasLayerKey, IcasToggles } from './types';

const STORAGE_KEY = 'forest.icas.toggles';

const DEFAULT_TOGGLES: IcasToggles = {
  entitati: false,
  trupuri: false,
  stands: false,
};

function readStored(): IcasToggles {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_TOGGLES;
    const parsed = JSON.parse(raw) as Partial<IcasToggles>;
    return {
      entitati: parsed.entitati === true,
      trupuri: parsed.trupuri === true,
      stands: parsed.stands === true,
    };
  } catch {
    return DEFAULT_TOGGLES;
  }
}

function writeStored(toggles: IcasToggles): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toggles));
  } catch {
    // ignore
  }
}

/**
 * Module-level store so every component that calls `useIcasToggles` in the
 * same tab observes the same toggle state. We can't just `useState` per
 * component — toggling in `<IcasLayerControls>` wouldn't notify
 * `<IcasLayers>` (the `storage` event only fires cross-tab, never within
 * the same tab), so the user would have to reload to see the layers
 * change. `useSyncExternalStore` makes the store a true single source of
 * truth without needing a Context provider.
 */
type Listener = () => void;
const listeners = new Set<Listener>();
let snapshot: IcasToggles = readStored();

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): IcasToggles {
  return snapshot;
}

function emit(): void {
  for (const l of listeners) l();
}

function applyChange(next: IcasToggles): void {
  if (
    next.entitati === snapshot.entitati &&
    next.trupuri === snapshot.trupuri &&
    next.stands === snapshot.stands
  ) {
    return;
  }
  snapshot = next;
  writeStored(next);
  emit();
}

// Cross-tab sync: when another tab writes to localStorage, mirror it here.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    const next = readStored();
    if (
      next.entitati === snapshot.entitati &&
      next.trupuri === snapshot.trupuri &&
      next.stands === snapshot.stands
    ) {
      return;
    }
    snapshot = next;
    emit();
  });
}

/**
 * Per-user, per-origin persistence of ICAS overlay visibility. Shared across
 * every map surface (filter / parcel modal / polygon editor) so user choice
 * survives navigation, AND across every component within the same tab so
 * toggling the checkbox immediately re-renders the layer.
 */
export function useIcasToggles() {
  const toggles = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const setToggle = useCallback((key: IcasLayerKey, value: boolean) => {
    applyChange({ ...snapshot, [key]: value });
  }, []);

  return { toggles, setToggle };
}
